import * as Y from 'yjs';

// A path API over the same canonical Y.Doc. No mirrored text or network peer.
export class HosannaWorkspace {
  constructor(source, writable) {
    this.source = source;
    this.writable = writable;
    this.nodes = source.getMap('nodes');
    this.pathIndex = null;
    this.currentTransaction = null;
    source.on('beforeTransaction', transaction => {this.currentTransaction = transaction;});
    this.nodes.observe(() => {this.pathIndex = null;});
    source.on('afterTransaction', () => {this.currentTransaction = null;});
    this.ui = source.getMap('workspace-ui');
    this.emptyTexts = new Map();
    this.placeholders = new Y.Doc();
    this.listeners = new Map();
    this.maps = new Map();
    this.files = this.virtualMap(() => new Map(this.paths().map(([path]) => [path, true])),
      path => this.ensureFile(path), path => this.deletePath(path), this.nodes);
    this.doc = new Proxy(source, {get: (target, key) => {
      if (key === 'getText') return name => this.text(name);
      if (key === 'getMap') return name => this.map(name);
      if (key === 'on' || key === 'off') return (event, listener) => this.listen(key, event, listener);
      const value = target[key];
      return typeof value === 'function' ? value.bind(target) : value;
    }});
  }
  path(nodeId) {
    const parts = [], seen = new Set();
    while (nodeId) {
      if (seen.has(nodeId)) return null;
      seen.add(nodeId);
      const node = this.nodes.get(nodeId);
      if (!node || !node.name || /[\\/\x00-\x1f]/.test(node.name)) return null;
      parts.unshift(node.name); nodeId = node.parentId;
    }
    return parts.join('/');
  }
  index() {
    // Y.Map mutations are visible immediately, including inside transactions.
    // Its observe callback runs only at transaction end, so use changed keys to
    // invalidate the cache during a transaction as well as between transactions.
    const transaction = this.currentTransaction;
    const changed = transaction?.changed?.has(this.nodes);
    if (changed) this.pathIndex = null;
    if (!this.pathIndex) {
      const byPath = new Map(), files = [];
      for (const node of this.nodes.values()) {
        const path = this.path(node.id);
        if (!path) continue;
        if (!byPath.has(path)) byPath.set(path, node.id);
        if (node.type === 'file' && /\.txt$/i.test(node.name)) files.push([path, node.id]);
      }
      this.pathIndex = {byPath, files};
    }
    return this.pathIndex;
  }
  paths() {
    return this.index().files.slice();
  }
  id(path) {
    return this.index().byPath.get(path) ?? null;
  }
  assertWritable() {
    if (!this.writable()) throw new Error('Poczekaj na wczytanie lokalnych danych lub pierwszą synchronizację z Hosanną.');
  }
  ensureFile(path) {
    this.assertWritable();
    const parts = path.split('/');
    if (!/\.txt$/i.test(path) || parts.some(part => !part || part === '.' || part === '..' || /[\\\x00-\x1f]/.test(part))) {
      throw new Error('Nieprawidłowa ścieżka śpiewnika.');
    }
    let parentId = null;
    this.source.transact(() => {
      parts.forEach((name, index) => {
        const type = index === parts.length - 1 ? 'file' : 'folder';
        const existing = [...this.nodes.values()].find(node => node.name === name && node.parentId === parentId);
        if (existing && existing.type !== type) throw new Error('Ścieżka koliduje z istniejącym plikiem.');
        const id = existing?.id || crypto.randomUUID();
        if (!existing) this.nodes.set(id, {id, name, parentId, type, createdBy: 'Chwała', createdAt: new Date().toISOString(), modifiedAt: new Date().toISOString(), modifiedBy: 'Chwała'});
        parentId = id;
      });
    });
    return parentId;
  }
  deletePath(path) {
    this.assertWritable();
    const id = this.id(path);
    if (!id) return false;
    if (this.nodes.get(id).type !== 'file') throw new Error('Usuń zawartość katalogu przed usunięciem katalogu.');
    this.source.transact(() => {
      const text = this.source.getText(`file:${id}`);
      text.delete(0, text.length);
      this.nodes.delete(id);
    });
    return true;
  }
  renamePath(oldPath, newPath) {
    this.assertWritable();
    const id = this.id(oldPath), node = this.nodes.get(id);
    if (!node) return false;
    if (this.id(newPath)) throw new Error('Plik o tej nazwie już istnieje.');
    if (!/\.txt$/i.test(newPath)) throw new Error('Śpiewnik musi mieć rozszerzenie .txt.');
    // Create any missing destination folders using a temporary file node, then
    // discard only that empty node. The actual file and its Y.Text keep their ID.
    this.source.transact(() => {
      const temporaryId = this.ensureFile(newPath);
      const destination = this.nodes.get(temporaryId);
      this.nodes.delete(temporaryId);
      this.nodes.set(id, {...node, name: destination.name, parentId: destination.parentId, modifiedAt: new Date().toISOString(), modifiedBy: 'Chwała'});
    });
    return true;
  }
  text(name) {
    if (!name.startsWith('content:')) return this.source.getText(name);
    const path = name.slice(8), id = this.id(path);
    if (id) { this.emptyTexts.delete(path); return this.source.getText(`file:${id}`); }
    // Deep links can mount before the first sync. Keep the placeholder on a
    // private doc so toString/observe do not warn, and never create an orphan
    // node or import old cached text just because a component reads a path.
    if (!this.emptyTexts.has(path)) this.emptyTexts.set(path, this.placeholders.getText(path));
    return this.emptyTexts.get(path);
  }
  virtualMap(read, write, remove, observed) {
    return {
      get: key => read().get(key), has: key => read().has(key), keys: () => read().keys(),
      values: () => read().values(), entries: () => read().entries(),
      forEach: callback => read().forEach(callback),
      set: (key, value) => {this.assertWritable(); write(key, value);},
      delete: key => {this.assertWritable(); return remove(key);},
      observe: listener => observed.observe(listener), unobserve: listener => observed.unobserve(listener),
      [Symbol.iterator]: () => read().entries(), get size() {return read().size;},
    };
  }
  selection() {
    try {
      const selection = JSON.parse(this.ui.get('adorans-active-song') || 'null');
      const fileName = selection && this.path(selection.nodeId);
      if (!fileName || !Number.isSafeInteger(selection.songPosition) || selection.songPosition < 1) return null;
      const groupTarget = JSON.parse(this.ui.get('group-follow-target') || 'null');
      const group = groupTarget?.endedAt ? null : groupTarget;
      return {fileName, songNumber: selection.songPosition,
        line: group?.nodeId === selection.nodeId ? group.line : undefined,
        groupEventId: group?.nodeId === selection.nodeId ? `${group.nonce ?? ''}:${group.updatedAt ?? ''}` : undefined,
        __processId: selection.sender?.startsWith('legacy:') ? selection.sender.slice(7) : selection.sender,
        bridgeEventId: selection.eventId, notifyOthers: true, sectionIndex: 0, sectionNumber: 1};
    } catch {return null;}
  }
  presentation() {
    try {
      const target = JSON.parse(this.ui.get('presentation-target') || 'null');
      const fileName = target && this.path(target.nodeId);
      if (target?.endedAt || !fileName || !['file', 'song', 'section'].includes(target.kind)
        || !Number.isSafeInteger(target.line) || target.line < 1
        || typeof target.actor !== 'string' || typeof target.clientId !== 'number'
        || typeof target.nonce !== 'number' || typeof target.updatedAt !== 'string') return null;
      return {fileName, songNumber: target.songPosition, line: target.line,
        sectionIndex: target.kind === 'section' ? target.sectionIndex - 1 : undefined,
        kind: target.kind, canonicalTarget: target,
        chordsVisible: target.presentationShowChords ?? true,
        blankScreen: !!target.presentationBlankScreen,
        updatedAt: target.updatedAt, __processId: target.chwalaProcessId};
    } catch {return null;}
  }
  publishPresentation(value) {
    const nodeId = value && this.id(value.fileName);
    if (!nodeId || !Number.isSafeInteger(Number(value.songNumber)) || Number(value.songNumber) < 1
      || !Number.isSafeInteger(value.line) || value.line < 1) throw new Error('Nieprawidłowy cel prezentacji.');
    let previous = null;
    try { previous = JSON.parse(this.ui.get('presentation-target') || 'null'); } catch { /* A malformed old target must not block publication. */ }
    // Keep settings owned by Hosanna when Chwała changes only the target.
    const target = {...previous, nodeId, pluginId: 'songs-file', kind: value.kind || 'section',
      line: value.line, songPosition: Number(value.songNumber), sectionIndex: value.canonicalSectionIndex,
      actor: 'Chwała', clientId: this.source.clientID, nonce: Date.now(),
      updatedAt: new Date().toISOString(), chwalaProcessId: value.__processId};
    delete target.endedAt;
    if (typeof value.chordsVisible === 'boolean') target.presentationShowChords = value.chordsVisible;
    if (typeof value.blankScreen === 'boolean') target.presentationBlankScreen = value.blankScreen
      ? previous?.presentationBlankScreen || 'black' : undefined;
    this.ui.set('presentation-target', JSON.stringify(target));
  }
  map(name) {
    if (name === 'files') return this.files;
    if (this.maps.has(name)) return this.maps.get(name);
    let map;
    if (name === 'presentationState') {
      const observed = {observe: listener => {this.ui.observe(listener); this.nodes.observe(listener);},
        unobserve: listener => {this.ui.unobserve(listener); this.nodes.unobserve(listener);}};
      map = this.virtualMap(() => new Map(this.presentation() ? [['current', this.presentation()]] : []),
        (_, value) => this.publishPresentation(value), () => this.ui.delete('presentation-target'), observed);
    } else if (name === 'fileBrowserSelections') {
      const key = 'latestSelection';
      map = this.virtualMap(() => new Map(this.selection() ? [[key, this.selection()]] : []), (_, value) => {
        const nodeId = value && this.id(value.fileName);
        if (!nodeId || !Number.isSafeInteger(Number(value.songNumber)) || Number(value.songNumber) < 1) throw new Error('Nieprawidłowy wybór pieśni.');
        this.source.transact(() => {
          this.ui.set('adorans-active-song', JSON.stringify({nodeId, songPosition: Number(value.songNumber),
            sender: `legacy:${value.__processId || 'chwala'}`, eventId: crypto.randomUUID()}));
          // Hosanna marks the group target (and follows it in group mode) from
          // group-follow-target, so a selection without it stays invisible there.
          if (Number.isSafeInteger(value.line) && value.line > 0) {
            this.ui.set('group-follow-target', JSON.stringify({actor: 'Chwała', clientId: this.source.clientID,
              line: value.line, nodeId, nonce: Date.now(), updatedAt: new Date().toISOString()}));
          }
        });
      }, () => this.ui.delete('adorans-active-song'), {
        observe: listener => {this.ui.observe(listener); this.nodes.observe(listener);},
        unobserve: listener => {this.ui.unobserve(listener); this.nodes.unobserve(listener);},
      });
    } else if (name.startsWith('metadata:')) {
      const path = name.slice(9);
      const read = () => {
        const node = this.nodes.get(this.id(path));
        return new Map(node ? Object.entries({...node.chwalaMetadata, lastEditTime: node.modifiedAt, author: node.modifiedBy}) : []);
      };
      map = this.virtualMap(read, (key, value) => {
        const id = this.id(path), node = this.nodes.get(id);
        if (!node) return;
        const chwalaMetadata = {...node.chwalaMetadata, [key]: value};
        this.nodes.set(id, {...node, chwalaMetadata,
          ...(key === 'lastEditTime' ? {modifiedAt: value} : {}), ...(key === 'author' ? {modifiedBy: value} : {})});
      }, key => {
        const id = this.id(path), node = this.nodes.get(id);
        if (!node) return false;
        const chwalaMetadata = {...node.chwalaMetadata}; delete chwalaMetadata[key];
        this.nodes.set(id, {...node, chwalaMetadata}); return true;
      }, this.nodes);
    } else return this.source.getMap(name);
    this.maps.set(name, map); return map;
  }
  listen(method, event, listener) {
    if (event !== 'afterTransaction') {this.source[method](event, listener); return;}
    if (method === 'off') {
      const wrapped = this.listeners.get(listener);
      if (wrapped) this.source.off(event, wrapped);
      this.listeners.delete(listener); return;
    }
    const wrapped = transaction => {
      const changedParentTypes = new Map(transaction.changedParentTypes);
      if (changedParentTypes.has(this.nodes)) changedParentTypes.set(this.files, []);
      listener(new Proxy(transaction, {get: (target, key) => key === 'changedParentTypes' ? changedParentTypes : target[key]}));
    };
    this.listeners.set(listener, wrapped); this.source.on(event, wrapped);
  }
}
